import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { accessCheck } from "@/lib/api";
import {
  ArrowRight,
  CheckCircle,
  Flag,
  Fire,
  ShareNetwork,
  ChartBar,
  Eye,
  Heartbeat,
  StackSimple,
} from "@phosphor-icons/react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import HaveAQuestionBadge from "@/components/HaveAQuestionBadge";

// Public landing page. Witty + sarcastic copy speaking to the broader pain
// (not just the multi-date feature). 1600px max width.
export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [projectId, setProjectId] = useState("");
  const [email, setEmail] = useState("");
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const handleOpen = async (e) => {
    e.preventDefault();
    if (!projectId.trim() || !email.trim()) {
      toast.error("Project ID and email required");
      return;
    }
    setOpening(true);
    try {
      const res = await accessCheck(projectId.trim(), email.trim().toLowerCase());
      if (res.allowed) {
        navigate(`/project/${projectId.trim()}?email=${encodeURIComponent(email.trim().toLowerCase())}`);
      } else {
        toast.error("Email not on the distribution list for that project");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open project");
    } finally {
      setOpening(false);
    }
  };

  return (
    <div className="min-h-screen tg-public-bg">
      <PublicNav />

      {/* HERO */}
      <section className="mx-auto max-w-[1600px] px-4 pt-16 pb-24 md:pt-24 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Copy */}
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-700 mb-6 tg-shadow-soft">
              <span className="h-1.5 w-1.5 rounded-full tg-gradient" />
              Built for project teams who track more than a vibe
            </span>
            <h1 className="text-5xl md:text-7xl tracking-tight leading-[1.05] text-slate-950">
              Projects that{" "}
              <span className="tg-gradient-text">don't unravel.</span>
              <br />
              Standups you actually{" "}
              <span className="tg-gradient-text">finish.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Project tracking has been a thinly-veiled spreadsheet hostage
              situation since 2003. We made an opinionated Gantt that respects
              your time, your team, and the very simple fact that real
              projects have moving pieces. Shocking, we know.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/register">
                <Button
                  className="h-12 px-6 rounded-full text-white text-base font-medium tg-gradient tg-shadow-soft hover:opacity-95 border-0 gap-2"
                  data-testid="hero-cta-register"
                >
                  Make your first chart
                  <ArrowRight size={16} weight="bold" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button
                  variant="outline"
                  className="h-12 px-6 rounded-full text-base font-medium border-slate-300 hover:border-slate-950"
                  data-testid="hero-cta-pricing"
                >
                  See pricing
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-slate-600">
              <span className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-emerald-500" /> Free tier (10 projects)</span>
              <span className="flex items-center gap-2"><CheckCircle size={14} weight="fill" className="text-emerald-500" /> No card required</span>
            </div>
          </div>

          {/* Open shared project — kept from old landing */}
          <div className="relative">
            <div className="absolute -inset-4 tg-gradient rounded-3xl opacity-20 blur-2xl" />
            <form
              onSubmit={handleOpen}
              className="relative bg-white border border-slate-200 rounded-2xl p-8 tg-shadow-soft"
              data-testid="open-shared-project-form"
            >
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-sf-label">
                Already invited?
              </div>
              <h2 className="text-2xl mb-1">Open a shared project</h2>
              <p className="text-sm text-slate-500 mb-6">No account needed if you're on the distribution list.</p>

              <Label htmlFor="pid" className="text-xs uppercase tracking-widest text-slate-500 font-sf-label">Project ID</Label>
              <Input
                id="pid"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                placeholder="Paste project id or share link token"
                className="mt-1.5 rounded-xl h-11"
                data-testid="open-project-id"
              />

              <Label htmlFor="em" className="text-xs uppercase tracking-widest text-slate-500 font-sf-label mt-4 block">Your email</Label>
              <Input
                id="em"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1.5 rounded-xl h-11"
                data-testid="open-project-email"
              />

              <Button
                type="submit"
                disabled={opening}
                className="w-full mt-6 h-12 rounded-full text-white bg-slate-950 hover:bg-slate-800 border-0 tg-shadow-soft"
                data-testid="open-project-submit"
              >
                {opening ? "Checking..." : "Open project"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* FEATURES — re-imagined layout: a 3-column zig-zag with the centerpiece
          card spanning two rows. Each card sells a DIFFERENT feature. */}
      <section className="bg-white border-y border-slate-200 py-20" data-testid="features-section">
        <div className="mx-auto max-w-[1600px] px-4">
          <div className="flex flex-col lg:flex-row items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-3 font-sf-label">
                What you actually get
              </div>
              <h2 className="text-4xl md:text-5xl text-slate-950">
                Features that <span className="tg-gradient-text">earn their keep.</span>
              </h2>
            </div>
            <p className="max-w-md text-slate-600 lg:text-right">
              No AI buzzwords. No "synergy." Just the stuff that makes standups
              feel less like you're trying to calculate hawking radiation.
            </p>
          </div>

          {/* Asymmetric feature grid */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-5 auto-rows-[minmax(160px,_auto)]">
            {/* Big centerpiece card */}
            <FeatureBig
              icon={Fire}
              tag="Risk roll-up"
              title="Risk heatmap that doesn't lie."
              copy="See exactly which workstream is on fire, in which week, with which severity. Click a cell to jump straight to the offending task. Beats squinting at a status doc your VP never reads."
              className="md:col-span-3 md:row-span-2"
              gradient="from-rose-500 via-fuchsia-500 to-violet-600"
            />
            <FeatureCompact
              icon={Flag}
              tag="On-chart markers"
              title="Milestones, issues, defects."
              copy="Drop event icons right on the bar — the chart becomes the source of truth, not a side document."
              className="md:col-span-3"
            />
            <FeatureCompact
              icon={ShareNetwork}
              tag="Sharing"
              title="Distribute by email."
              copy="Add teammates to a distribution list with view/edit/admin roles. No 'invite link' theatre."
              className="md:col-span-3"
            />
            <FeatureCompact
              icon={Eye}
              tag="Saved views"
              title="One chart, many lenses."
              copy="Filter by workstream, status, or your own tag. Save the view, share the link, ship the meeting."
              className="md:col-span-2"
            />
            <FeatureCompact
              icon={Heartbeat}
              tag="Health tracker"
              title="Stoplight at a glance."
              copy="A green/yellow/red pill at the top of every project so you know the verdict before the meeting starts."
              className="md:col-span-2"
            />
            <FeatureCompact
              icon={StackSimple}
              tag="Dependencies"
              title="Subtasks, deps, critical path."
              copy="Drag arrows between tasks. The math runs in real-time. Subtask nesting that doesn't crumble."
              className="md:col-span-2"
            />
            <FeatureCompact
              icon={ChartBar}
              tag="Export"
              title="PDF, PNG, CSV, XLSX."
              copy="Every chart, every view, every cell. The export button was the first one we built — on purpose."
              className="md:col-span-3"
            />
            <FeatureCompact
              icon={ShareNetwork}
              tag="Activity feed"
              title="Know what changed."
              copy="A bell with a real changelog: every task created, edited, deleted, or reordered. No more 'who moved my deadline?'"
              className="md:col-span-3"
            />
          </div>
        </div>
      </section>

      {/* VALUE PROP */}
      <section className="py-24" data-testid="value-section">
        <div className="mx-auto max-w-[1600px] px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-3 font-sf-label">
              Why bother
            </div>
            <h2 className="text-4xl md:text-5xl text-slate-950 mb-6">
              Manage your project's <span className="tg-gradient-text">inevitable failure</span> with slightly more dignity.
            </h2>
            <ul className="space-y-5">
              <Value
                title="Spreadsheets are for accountants."
                copy="They make beautiful tax returns. They make terrible Gantt charts. We made a tool that lets the chart hold dependencies, dates, and risks without the cells fighting each other."
              />
              <Value
                title="Sharing should not require a Zoom call."
                copy="Distribute by email. Roles enforced. Comments and labels live inline. The PMO doesn't need to host a 'walkthrough' every Tuesday."
              />
              <Value
                title="The future was supposed to be lightweight."
                copy="No 47-step onboarding. No 'AI agent will draft your timeline' nonsense. Open the app, drag a date, ship the project. Revolutionary."
              />
            </ul>
            <Link to="/register" className="inline-flex mt-8">
              <Button
                className="h-12 px-6 rounded-full text-white text-base font-medium tg-gradient tg-shadow-soft hover:opacity-95 border-0 gap-2"
                data-testid="value-cta"
              >
                Try it free
                <ArrowRight size={16} weight="bold" />
              </Button>
            </Link>
          </div>

          {/* Visual: stylized gantt-like preview */}
          <div className="relative">
            <div className="absolute -inset-6 tg-gradient rounded-3xl opacity-25 blur-3xl" />
            <div className="relative bg-white border border-slate-200 rounded-2xl p-6 tg-shadow-soft">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 mb-4 font-sf-label">
                <span>Project · Q2 launch</span>
                <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> ON TRACK</span>
              </div>
              <div className="space-y-3">
                {[
                  { name: "Discovery & scoping", width: 55, color: "from-violet-400 to-indigo-500" },
                  { name: "Design system", width: 75, color: "from-indigo-400 to-blue-500" },
                  { name: "Backend buildout", width: 40, color: "from-blue-400 to-cyan-500" },
                  { name: "QA + UAT", width: 25, color: "from-cyan-400 to-teal-500" },
                  { name: "Go-live prep", width: 10, color: "from-fuchsia-400 to-pink-500" },
                ].map((r) => (
                  <div key={r.name} className="flex items-center gap-3">
                    <div className="w-32 text-xs text-slate-700 truncate shrink-0">{r.name}</div>
                    <div className="flex-1 h-7 bg-slate-100 rounded-md overflow-hidden">
                      <div
                        className={`h-full bg-gradient-to-r ${r.color} rounded-md`}
                        style={{ width: `${r.width}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 font-sf-label">
                <span>14 tasks · 3 workstreams</span>
                <span className="text-slate-400">— roughly what a Tuesday standup looks like</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
      <HaveAQuestionBadge />
    </div>
  );
}

// ---------- feature card variants ----------

function FeatureBig({ icon: Icon, tag, title, copy, className = "", gradient }) {
  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${gradient} text-white p-8 flex flex-col justify-between tg-shadow-soft ${className}`}
      data-testid={`feature-${tag.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/70 mb-3 font-sf-label">
          {tag}
        </div>
        <h3 className="text-2xl md:text-3xl text-white mb-4 leading-tight">
          {title}
        </h3>
        <p className="text-sm text-white/85 leading-relaxed max-w-md">{copy}</p>
      </div>
      <div className="mt-6 flex items-center justify-between">
        <Icon size={42} weight="fill" className="text-white/90" />
        <ArrowRight size={18} weight="bold" className="text-white/70" />
      </div>
      <div className="absolute -bottom-12 -right-12 w-44 h-44 bg-white/15 rounded-full blur-2xl" />
    </div>
  );
}

function FeatureCompact({ icon: Icon, tag, title, copy, className = "" }) {
  return (
    <div
      className={`group rounded-2xl bg-white border border-slate-200 p-6 transition-all hover:border-slate-300 hover:tg-shadow-soft flex flex-col ${className}`}
      data-testid={`feature-${tag.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`h-10 w-10 rounded-xl tg-gradient flex items-center justify-center text-white group-hover:scale-105 transition-transform`}>
          <Icon size={16} weight="fill" />
        </div>
        <span className="text-[9px] uppercase tracking-widest text-slate-400 font-sf-label">{tag}</span>
      </div>
      <h3 className="text-lg font-semibold text-slate-950 mb-1">{title}</h3>
      <p className="text-sm text-slate-600 leading-relaxed flex-1">{copy}</p>
    </div>
  );
}

function Value({ title, copy }) {
  return (
    <li>
      <div className="flex items-start gap-3">
        <span className="h-6 w-6 rounded-full tg-gradient flex items-center justify-center shrink-0 mt-0.5">
          <CheckCircle size={12} weight="fill" className="text-white" />
        </span>
        <div>
          <div className="text-base font-semibold text-slate-950">{title}</div>
          <div className="mt-1 text-sm text-slate-600 leading-relaxed">{copy}</div>
        </div>
      </div>
    </li>
  );
}
